import { useContext, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";

import { resolveDocRef } from "./resolveDocRef";

const recordDebounceMs = 1200;
const recentlyViewedQueryKey = ["docs", "getRecentlyViewed"];

export const useRecordRecentlyViewed = () => {
  const auth = useContext(IamAuthContext);
  const location = useLocation();
  const queryClient = useQueryClient();
  const recordView = useClientManager("docs").recordView().useMutation();

  const lastRecordedRef = useRef<string | undefined>(undefined);
  const recordViewRef = useRef(recordView);
  recordViewRef.current = recordView;

  const isAuthenticated = Boolean(auth.iamUser);

  useEffect(() => {
    if (!isAuthenticated) return;

    const timeout = setTimeout(async () => {
      const docRef = resolveDocRef(location.pathname);

      if (docRef && docRef.docId !== lastRecordedRef.current) {
        const isRecorded = await recordViewRef.current
          .mutateAsync({ docId: docRef.docId, docType: docRef.docType })
          .then(() => true)
          .catch(() => false);
        if (isRecorded) {
          lastRecordedRef.current = docRef.docId;
        }
      } else if (!docRef) {
        lastRecordedRef.current = undefined;
      }

      await queryClient.refetchQueries({ queryKey: recentlyViewedQueryKey });
    }, recordDebounceMs);

    return () => clearTimeout(timeout);
  }, [location.pathname, isAuthenticated, queryClient]);
};
